/**
 * Slurp admin panel — treasury wallet only.
 *
 * Auth model: stateless wallet-signature challenge. Every API request signs
 * a fresh `slurp-admin:<unix-ms>` and the backend re-verifies. Admin pubkey
 * is allow-listed server-side, so the client can't bypass anything by
 * editing JS or hitting the URL directly.
 *
 * The signed headers are cached in adminApi for ~4 minutes so the user
 * isn't prompted on every endpoint hit.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useWallet, useConnection } from "@solana/wallet-adapter-react";
import { ChevronDown } from "lucide-react";
import { adminApi, clearAdminAuthCache } from "../../lib/adminApi";
import { batchClaimSlurpFees, claimSingleSlurpFee } from "../../lib/claimSlurpFees";
import { sendBatchPayouts } from "../../lib/batchPayout";

const TREASURY_ADDRESS = "FaDBjsBmzbGAMYUPKacNbm53Fe4ZoEfJMZVhuU4YHR8J";

const fmtUsd = (n) => {
  const v = Number(n) || 0;
  if (Math.abs(v) >= 1e9) return `$${(v / 1e9).toFixed(2)}B`;
  if (Math.abs(v) >= 1e6) return `$${(v / 1e6).toFixed(2)}M`;
  if (Math.abs(v) >= 1e3) return `$${(v / 1e3).toFixed(1)}K`;
  return `$${v.toFixed(2)}`;
};
const fmtSol = (n) => {
  const v = Number(n) || 0;
  if (v >= 1000) return `${v.toFixed(0)} SOL`;
  if (v >= 1)    return `${v.toFixed(2)} SOL`;
  return `${v.toFixed(4)} SOL`;
};
const fmtCount = (n) => Number(n || 0).toLocaleString();
const shortMint = (m) => (m ? `${m.slice(0, 4)}…${m.slice(-4)}` : "—");
const shortSig  = (s) => (s ? `${s.slice(0, 6)}…${s.slice(-4)}` : "—");
const fmtAgo = (iso) => {
  if (!iso) return "—";
  const ms = Date.now() - new Date(iso).getTime();
  const s = Math.max(0, Math.floor(ms / 1000));
  if (s < 60)    return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60)    return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24)    return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

// Countdown string for a future timestamp, e.g. "3d 14h" / "5h 22m" / "8m".
// Returns "Ready now" if the target is in the past.
const fmtCountdown = (target) => {
  if (!target) return "—";
  const ms = new Date(target).getTime() - Date.now();
  if (ms <= 0) return "Ready now";
  const s = Math.floor(ms / 1000);
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m`;
  return `<1m`;
};

// Dark slurp-styled modal shell. Used by both MarkPaidModal and
// ConfirmModal. Standalone class set so it doesn't inherit the
// cream-paper `.pm-modal` text colors used elsewhere.
function AdminModal({ children, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="adm-modal-bg" onClick={onClose} role="dialog" aria-modal="true">
      <div className="adm-modal-card" onClick={(e) => e.stopPropagation()}>
        <button className="adm-modal-close" onClick={onClose} aria-label="Close">×</button>
        {children}
      </div>
    </div>
  );
}

// Confirmation modal — for irreversible actions like Draw Winner.
function ConfirmModal({ title, body, confirmLabel, danger, onConfirm, onClose, submitting }) {
  return (
    <AdminModal onClose={onClose}>
      <h3 className="adm-modal-title">{title}</h3>
      {body && <p className="adm-modal-body">{body}</p>}
      <div className="adm-modal-actions">
        <button type="button" className="adm__btn adm__btn--ghost" onClick={onClose}>
          Cancel
        </button>
        <button
          type="button"
          className={`adm__btn ${danger ? "adm__btn--danger" : "adm__btn--primary"}`}
          onClick={onConfirm}
          disabled={submitting}
        >
          {submitting ? "Working…" : (confirmLabel || "Confirm")}
        </button>
      </div>
    </AdminModal>
  );
}

// Mark-paid modal with amount + optional tx fields. Tx defaults to
// optional; the public page just shows the wallet → solscan link
// regardless of whether a tx is recorded.
function MarkPaidModal({
  title,
  subtitle,
  amountLabel,
  defaultAmount,
  showNote,
  submitting,
  onSubmit,
  onClose,
}) {
  const [amount, setAmount] = useState(defaultAmount || "");
  const [tx, setTx] = useState("");
  const [note, setNote] = useState("");
  const [err, setErr] = useState(null);

  const handleSubmit = (e) => {
    e?.preventDefault?.();
    setErr(null);
    const amt = Number(amount);
    if (!Number.isFinite(amt) || amt <= 0) {
      setErr("Amount must be a positive number.");
      return;
    }
    const cleanedTx = tx.trim();
    // Solana sigs are base58-encoded 64-byte signatures = 87-88 chars.
    // Accept 86-90 to allow trailing whitespace or odd casings, else
    // bounce — a too-short input means a typo / partial paste.
    if (cleanedTx && (cleanedTx.length < 86 || cleanedTx.length > 90)) {
      setErr("Tx signature should be ~88 base58 chars (or leave blank).");
      return;
    }
    onSubmit({ amountSol: amt, tx: cleanedTx, note: note.trim() });
  };

  return (
    <AdminModal onClose={onClose}>
      <h3 className="adm-modal-title">{title}</h3>
      {subtitle && <p className="adm-modal-body adm-modal-body--mono">{subtitle}</p>}
      <form onSubmit={handleSubmit} className="adm-modal-form">
        <label className="adm-modal-field">
          <span>{amountLabel || "Amount paid (SOL)"}</span>
          <input
            type="text"
            inputMode="decimal"
            autoFocus
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="0.000000"
          />
        </label>
        <label className="adm-modal-field">
          <span>Tx signature (optional)</span>
          <input
            type="text"
            value={tx}
            onChange={(e) => setTx(e.target.value)}
            placeholder="paste sig or leave blank"
          />
        </label>
        {showNote && (
          <label className="adm-modal-field">
            <span>Note (optional)</span>
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. week 23 payout"
            />
          </label>
        )}
        {err && <div className="adm-modal-err">{err}</div>}
        <div className="adm-modal-actions">
          <button type="button" className="adm__btn adm__btn--ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="adm__btn adm__btn--primary" disabled={submitting}>
            {submitting ? "Recording…" : "Confirm Paid"}
          </button>
        </div>
      </form>
    </AdminModal>
  );
}

// Sticker stat — cream paper, ink border, chunky offset shadow.
function Sticker({ label, value, sub, accent, tilt }) {
  const accentClass =
    accent === "orange" ? "adm-sticker--orange"
    : accent ? "adm-sticker--accent"
    : "";
  return (
    <div
      className={`adm-sticker ${accentClass}`}
      style={tilt ? { transform: `rotate(${tilt}deg)` } : undefined}
    >
      <div className="adm-sticker__label">{label}</div>
      <div className="adm-sticker__value">{value}</div>
      {sub && <div className="adm-sticker__sub">{sub}</div>}
    </div>
  );
}

export default function AdminPage() {
  const wallet = useWallet();
  const { connection } = useConnection();
  const address = wallet.publicKey?.toBase58?.() || null;
  const isTreasury = address === TREASURY_ADDRESS;

  const [stats, setStats]         = useState(null);
  const [claimable, setClaimable] = useState(null);
  const [recent, setRecent]       = useState(null);
  const [buckets, setBuckets]     = useState(null);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState(null);
  const [signedIn, setSignedIn]   = useState(false);
  // Users-export modal: paginated list of every wallet that's ever traded
  // a slurp token. Opened from the header button. Pagination + search
  // state is owned by the modal itself.
  const [usersOpen, setUsersOpen] = useState(false);
  // Jackpot draw state — defaults to the CURRENT calendar month. The
  // operator can pick a different month from the dropdown if they're
  // running a late draw for a month that already ended.
  const [jpMonth, setJpMonth] = useState(() => {
    const d = new Date();
    return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
  });

  // Next jackpot draw — first one locked to 2026-07-31 UTC, then end
  // of every calendar month UTC. Must stay in sync with JackpotPage.js
  // (both use UTC so operator and public worldwide see the same date).
  const nextJackpotDraw = useMemo(() => {
    const FIRST = new Date(Date.UTC(2026, 6, 31, 23, 59, 0));
    const now = new Date();
    const eom = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 0, 23, 59, 0));
    return eom < FIRST ? FIRST : eom;
  }, []);
  // nextLeaderboardSnapshot computed below — `snapshotWeeks` is
  // declared further down so the useMemo must come after it.
  const [jpEligible, setJpEligible] = useState(null);
  const [jpDrawing, setJpDrawing] = useState(false);
  const [jpWinner, setJpWinner] = useState(null);
  const [jpPaying, setJpPaying] = useState(false);
  const [jpConfirmDraw, setJpConfirmDraw] = useState(false);
  // All unpaid jackpot drafts across all months. Powers the banner at
  // the top of the Jackpot panel so the operator never silently misses
  // an old unpaid month.
  const [jpUnpaidDrafts, setJpUnpaidDrafts] = useState([]);
  // Diamond Hands prize-pool snapshots (weekly leaderboards taken by the
  // backend). The operator marks rows paid here after the off-chain SOL
  // transfer lands.
  const [snapshotWeeks, setSnapshotWeeks] = useState([]);
  // Next leaderboard snapshot — last snapshot's week_start + 7 days.
  // If no snapshots exist yet, the indexer takes one on its next tick.
  const nextLeaderboardSnapshot = useMemo(() => {
    if (!snapshotWeeks.length) return null;
    const last = snapshotWeeks
      .map((w) => new Date(w.week_start).getTime())
      .reduce((a, b) => Math.max(a, b), 0);
    return new Date(last + 7 * 24 * 3600_000);
  }, [snapshotWeeks]);
  const [activeWeek, setActiveWeek] = useState(null);
  const [activeWinners, setActiveWinners] = useState(null);
  const [activePot, setActivePot] = useState(null); // { pot_owed_sol, pot_owed_usd, sol_usd }
  const [winnersLoading, setWinnersLoading] = useState(false);
  const [payingId, setPayingId] = useState(null);
  // Modal state for marking an individual leaderboard winner paid.
  // Pre-fills with the backend's suggested payout so the operator just
  // confirms the amount (and optionally pastes a tx).
  const [lbPayModal, setLbPayModal] = useState(null); // null | { winner, defaultAmt }

  const [claimingAll, setClaimingAll] = useState(false);
  const [claimingMint, setClaimingMint] = useState(null);
  const [queueQuery, setQueueQuery] = useState("");
  const [recentQuery, setRecentQuery] = useState("");
  const [recentOpen, setRecentOpen] = useState(false);
  // 1-minute ticker so countdown timers re-render live without rebuilding
  // any data. Just bumps state.
  const [, setNowTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setNowTick((n) => n + 1), 60_000);
    return () => clearInterval(id);
  }, []);

  // Platform-wide broadcast composer. One row in `notifications` with
  // recipient='*' fans out to every connected wallet's bell.
  const [bcTitle, setBcTitle] = useState("");
  const [bcBody, setBcBody] = useState("");
  const [bcLink, setBcLink] = useState("");
  const [bcSending, setBcSending] = useState(false);
  const [bcError, setBcError] = useState(null);
  const [bcLastSentAt, setBcLastSentAt] = useState(null);
  const [bcConfirm, setBcConfirm] = useState(null); // null | { title, body, link }

  const ctx = useMemo(
    () => ({ address, signMessage: wallet.signMessage }),
    [address, wallet.signMessage]
  );

  // Defined after `ctx` because the callback closes over it; declaring
  // sendBroadcast first would TDZ-crash on first render.
  const openBroadcastConfirm = useCallback(() => {
    const title = bcTitle.trim();
    const body  = bcBody.trim();
    const link  = bcLink.trim();
    if (!title) { setBcError("Title is required."); return; }
    if (link) {
      try { new URL(link); } catch {
        setBcError("Link is not a valid URL.");
        return;
      }
      if (!/^https?:\/\//i.test(link)) {
        setBcError("Link must start with http:// or https://");
        return;
      }
    }
    setBcError(null);
    setBcConfirm({ title, body, link });
  }, [bcTitle, bcBody, bcLink]);

  const sendBroadcast = useCallback(async () => {
    if (!bcConfirm) return;
    const { title, body, link } = bcConfirm;
    setBcSending(true);
    setBcError(null);
    try {
      await adminApi.broadcast(ctx, { title, body: body || undefined, link: link || undefined });
      setBcTitle("");
      setBcBody("");
      setBcLink("");
      setBcLastSentAt(new Date());
      setBcConfirm(null);
    } catch (e) {
      setBcError(e?.message || "Broadcast failed");
    } finally {
      setBcSending(false);
    }
  }, [bcConfirm, ctx]);

  const filteredPools = useMemo(() => {
    const list = claimable?.pools || [];
    const q = queueQuery.trim().toLowerCase();
    if (!q) return list;
    return list.filter((p) => {
      const name = (p.name || "").toLowerCase();
      const sym = (p.symbol || "").toLowerCase();
      const mint = (p.mint || "").toLowerCase();
      return name.includes(q) || sym.includes(q) || mint.includes(q);
    });
  }, [claimable, queueQuery]);

  const filteredRecent = useMemo(() => {
    const list = recent?.claims || [];
    const q = recentQuery.trim().toLowerCase();
    if (!q) return list;
    return list.filter((c) => {
      const sym = (c.symbol || "").toLowerCase();
      const mint = (c.mint || "").toLowerCase();
      const sig = (c.signature || "").toLowerCase();
      return sym.includes(q) || mint.includes(q) || sig.includes(q);
    });
  }, [recent, recentQuery]);

  const refresh = useCallback(async () => {
    if (!isTreasury) return;
    setLoading(true);
    setError(null);
    try {
      const [s, c, r, snaps, b] = await Promise.all([
        adminApi.stats(ctx),
        adminApi.claimable(ctx),
        adminApi.recentClaims(ctx, 20),
        adminApi.snapshots(ctx),
        adminApi.buckets(ctx),
      ]);
      setStats(s);
      setClaimable(c);
      setRecent(r);
      setSnapshotWeeks(snaps?.weeks ?? []);
      setBuckets(b);
      setSignedIn(true);
      // If a week is currently open, refresh its winners/pot too so
      // suggested payouts reflect any new claims since it was opened.
      if (activeWeek) {
        try {
          const res = await adminApi.snapshot(ctx, activeWeek);
          setActiveWinners(res?.winners ?? []);
          setActivePot({
            pot_owed_sol: res?.pot_owed_sol ?? 0,
            pot_owed_usd: res?.pot_owed_usd ?? 0,
            sol_usd: res?.sol_usd ?? 0,
          });
        } catch { /* leave stale, surface main error path */ }
      }
    } catch (err) {
      setError(err?.message || String(err));
    } finally {
      setLoading(false);
    }
  }, [ctx, isTreasury, activeWeek]);

  const openUsers = useCallback(() => {
    setUsersOpen(true);
  }, []);

  // Wipe the signature cache when the connected wallet changes / disconnects
  // so a different user never reuses the prior signature.
  useEffect(() => {
    return () => clearAdminAuthCache();
  }, []);
  useEffect(() => {
    clearAdminAuthCache();
    setSignedIn(false);
  }, [address]);

  const claimAll = useCallback(async () => {
    if (!claimable?.pools?.length) return;
    setClaimingAll(true);
    setError(null);
    try {
      const results = await batchClaimSlurpFees({
        wallet,
        pools: claimable.pools,
      });
      const failed = results.filter((r) => r.error);
      if (failed.length) {
        setError(`${failed.length} of ${results.length} claims failed: ${failed[0].error}`);
      }
      setTimeout(refresh, 1500);
    } catch (err) {
      setError(err?.message || String(err));
    } finally {
      setClaimingAll(false);
    }
  }, [claimable, wallet, refresh]);

  const claimOne = useCallback(async (pool) => {
    setClaimingMint(pool.mint);
    setError(null);
    try {
      await claimSingleSlurpFee({ wallet, pool });
      setTimeout(refresh, 1500);
    } catch (err) {
      setError(err?.message || String(err));
    } finally {
      setClaimingMint(null);
    }
  }, [wallet, refresh]);

  // Diamond Hands snapshot handlers
  const openWeek = useCallback(async (weekStart) => {
    setActiveWeek(weekStart);
    setActiveWinners(null);
    setActivePot(null);
    setWinnersLoading(true);
    try {
      const res = await adminApi.snapshot(ctx, weekStart);
      setActiveWinners(res?.winners ?? []);
      setActivePot({
        pot_owed_sol: res?.pot_owed_sol ?? 0,
        pot_owed_usd: res?.pot_owed_usd ?? 0,
        sol_usd: res?.sol_usd ?? 0,
      });
    } catch (err) {
      setError(err?.message || String(err));
    } finally {
      setWinnersLoading(false);
    }
  }, [ctx]);

  const markPaid = useCallback((winner) => {
    setLbPayModal({
      winner,
      defaultAmt: winner.suggested_payout_sol > 0
        ? winner.suggested_payout_sol.toFixed(6)
        : "",
    });
  }, []);

  const submitLeaderboardWinnerPaid = useCallback(async ({ amountSol, tx }) => {
    if (!lbPayModal) return;
    setPayingId(lbPayModal.winner.id);
    try {
      await adminApi.markPaid(ctx, lbPayModal.winner.id, tx, amountSol);
      setLbPayModal(null);
      // Refresh week winners, top snapshot list (for paid_count), and
      // buckets in parallel so all panels reflect the new state.
      const [snaps, b] = await Promise.all([
        adminApi.snapshots(ctx),
        adminApi.buckets(ctx),
      ]);
      setSnapshotWeeks(snaps?.weeks ?? []);
      setBuckets(b);
      if (activeWeek) await openWeek(activeWeek);
    } catch (err) {
      setError(err?.message || String(err));
    } finally {
      setPayingId(null);
    }
  }, [ctx, lbPayModal, activeWeek, openWeek]);

  // Auto-pay all unpaid leaderboard winners in the active week. Builds
  // one tx with N SystemProgram.transfer ixs, treasury signs once,
  // sig + per-winner amounts get recorded server-side.
  const [autoPaying, setAutoPaying] = useState(false);
  const [autoPayConfirm, setAutoPayConfirm] = useState(null); // null | { kind, payouts, totalSol }

  const payAllLeaderboard = useCallback(async () => {
    if (!activeWeek || !autoPayConfirm) return;
    setAutoPaying(true);
    setError(null);
    // Snapshot what we're paying so the modal can close immediately.
    // Prevents a tab-close-mid-flow → reopen scenario where the modal
    // is still showing the old payouts and a second sign could fire.
    const week = activeWeek;
    const payouts = autoPayConfirm.payouts;
    try {
      const sig = await sendBatchPayouts(connection, wallet, payouts);
      // Tx is on-chain → close the modal NOW so re-render can't sign
      // a second batch. The DB record happens next; if it fails, we
      // surface the error but the on-chain payment already happened.
      setAutoPayConfirm(null);
      await adminApi.markBatchPaid(ctx, week, {
        tx: sig,
        payouts: payouts.map((p) => ({
          id: p.id,
          amount_sol: p.amount_sol,
        })),
      });
      const [snaps, b] = await Promise.all([
        adminApi.snapshots(ctx),
        adminApi.buckets(ctx),
      ]);
      setSnapshotWeeks(snaps?.weeks ?? []);
      setBuckets(b);
      await openWeek(week);
    } catch (err) {
      setError(err?.message || String(err));
    } finally {
      setAutoPaying(false);
    }
  }, [activeWeek, autoPayConfirm, connection, wallet, ctx, openWeek]);

  const openLeaderboardAutoPay = useCallback(() => {
    const unpaid = (activeWinners ?? []).filter(
      (w) => !w.paid_at && (w.suggested_payout_sol ?? 0) > 0
    );
    if (!unpaid.length) return;
    const payouts = unpaid.map((w) => ({
      id: w.id,
      wallet: w.wallet,
      amount_sol: w.suggested_payout_sol,
      rank: w.rank,
    }));
    const totalSol = payouts.reduce((s, p) => s + p.amount_sol, 0);
    setAutoPayConfirm({ kind: "leaderboard", payouts, totalSol });
  }, [activeWinners]);

  // Loader for the unpaid-drafts banner. Declared here BEFORE
  // payJackpotAuto + drawJackpotWinner so those callbacks can include
  // it in their dep arrays without a TDZ crash.
  const loadUnpaidDrafts = useCallback(async () => {
    if (!isTreasury) return;
    try {
      const res = await adminApi.jackpotUnpaidDrafts(ctx);
      setJpUnpaidDrafts(res?.drafts ?? []);
    } catch { /* silent — banner just won't show */ }
  }, [ctx, isTreasury]);
  useEffect(() => { loadUnpaidDrafts(); }, [loadUnpaidDrafts]);

  // Confirm-modal state for delete actions (jackpot draft + leaderboard week).
  // Each one carries the target id/week + a callback that fires on confirm.
  const [deleteConfirm, setDeleteConfirm] = useState(null); // null | { title, body, onConfirm }
  const [deleting, setDeleting] = useState(false);

  const askDeleteJackpotDraft = useCallback((draft) => {
    setDeleteConfirm({
      title: "Delete jackpot draft?",
      body: `Wallet ${draft.winner_wallet.slice(0, 4)}…${draft.winner_wallet.slice(-4)} drawn for ${draft.month}. This hard-deletes the row and any matching bucket_payouts entry. Can't be undone.`,
      onConfirm: async () => {
        setDeleting(true);
        try {
          await adminApi.jackpotDeleteWinner(ctx, draft.id);
          await loadUnpaidDrafts();
          if (jpWinner?.id === draft.id) setJpWinner(null);
          setDeleteConfirm(null);
        } catch (err) {
          setError(err?.message || String(err));
        } finally {
          setDeleting(false);
        }
      },
    });
  }, [ctx, loadUnpaidDrafts, jpWinner]);

  const askDeleteLeaderboardWeek = useCallback((weekStart) => {
    setDeleteConfirm({
      title: "Delete leaderboard week?",
      body: `Removes ALL snapshot rows for ${new Date(weekStart).toISOString().slice(0, 16).replace("T", " ")} UTC and any matching bucket_payouts entries. Can't be undone.`,
      onConfirm: async () => {
        setDeleting(true);
        try {
          await adminApi.leaderboardDeleteWeek(ctx, weekStart);
          const [snaps, b] = await Promise.all([
            adminApi.snapshots(ctx),
            adminApi.buckets(ctx),
          ]);
          setSnapshotWeeks(snaps?.weeks ?? []);
          setBuckets(b);
          if (activeWeek === weekStart) {
            setActiveWeek(null);
            setActiveWinners(null);
            setActivePot(null);
          }
          setDeleteConfirm(null);
        } catch (err) {
          setError(err?.message || String(err));
        } finally {
          setDeleting(false);
        }
      },
    });
  }, [ctx, activeWeek]);

  // Same flow for jackpot — single recipient, but reuses the batch
  // helper so the tx layout / sendTx / confirm code is shared.
  const payJackpotAuto = useCallback(async () => {
    if (!jpWinner) return;
    // Use the winner row's locked-at-draw amount when paying an old
    // draft (different month than the one currently accumulating);
    // fall back to current bucket owed for a same-month payout.
    const draftMatchesActiveMonth = (() => {
      if (!jpWinner.drawn_at) return true;
      const d = new Date(jpWinner.drawn_at);
      const drawnMonth = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
      const nowMonth = (() => {
        const n = new Date();
        return `${n.getUTCFullYear()}-${String(n.getUTCMonth() + 1).padStart(2, "0")}`;
      })();
      return drawnMonth === nowMonth;
    })();
    const amountSol = draftMatchesActiveMonth
      ? (buckets?.buckets?.jackpot?.owed_sol ?? 0)
      : Number(jpWinner.amount_sol || 0);
    if (amountSol <= 0) {
      setError("Pot for this draw is empty.");
      return;
    }
    setJpPaying(true);
    setError(null);
    try {
      const sig = await sendBatchPayouts(connection, wallet, [
        { wallet: jpWinner.winner_wallet, amount_sol: amountSol },
      ]);
      await adminApi.jackpotMarkWinnerPaid(ctx, {
        id: jpWinner.id,
        amountSol,
        tx: sig,
      });
      setJpWinner(null);
      const [b] = await Promise.all([
        adminApi.buckets(ctx),
        loadUnpaidDrafts(),
      ]);
      setBuckets(b);
    } catch (err) {
      setError(err?.message || String(err));
    } finally {
      setJpPaying(false);
    }
  }, [jpWinner, buckets, connection, wallet, ctx, loadUnpaidDrafts]);

  // Bucket payouts: leaderboard / jackpot. Operator pays out off-chain
  // then records the SOL amount + optional tx + note here so the owed
  // total drops.

  // Jackpot draw handlers ──────────────────────────────────────────────
  // Load eligible count + check if a draw already happened for the
  // selected month. Idempotent — safe to call repeatedly.
  const loadJackpotMonth = useCallback(async () => {
    if (!jpMonth) return;
    setError(null);
    try {
      const eligible = await adminApi.jackpotEligible(ctx, jpMonth);
      setJpEligible(eligible);
      // Backend tells us if a winner already exists for this month
      // (paid or unpaid). If so, surface it immediately so the operator
      // sees the unpaid card without having to click Draw Winner.
      if (eligible?.existing_winner && !eligible.existing_winner.paid_tx) {
        setJpWinner(eligible.existing_winner);
      } else {
        setJpWinner(null);
      }
    } catch (err) {
      setError(err?.message || String(err));
    }
  }, [ctx, jpMonth]);

  useEffect(() => { loadJackpotMonth(); }, [loadJackpotMonth]);


  const openDrawConfirm = useCallback(() => {
    if (!jpEligible?.eligible_count) return;
    setJpConfirmDraw(true);
  }, [jpEligible]);

  const drawJackpotWinner = useCallback(async () => {
    if (!jpEligible?.eligible_count) return;
    setJpDrawing(true);
    setError(null);
    try {
      const res = await adminApi.jackpotDraw(ctx, jpMonth, undefined);
      setJpWinner(res.winner);
      setJpConfirmDraw(false);
      const [b] = await Promise.all([
        adminApi.buckets(ctx),
        loadUnpaidDrafts(),
      ]);
      setBuckets(b);
    } catch (err) {
      setError(err?.message || String(err));
    } finally {
      setJpDrawing(false);
    }
  }, [ctx, jpMonth, jpEligible, loadUnpaidDrafts]);


  // Gate ─────────────────────────────────────────────────────────────────
  if (!address) {
    return (
      <div className="adm">
        <div className="adm__gate">
          <h1 className="adm__gate-title">LOGIN</h1>
          <p className="adm__gate-msg">Connect wallet.</p>
        </div>
      </div>
    );
  }
  if (!isTreasury) {
    return (
      <div className="adm">
        <div className="adm__gate">
          <h1 className="adm__gate-title">LOGIN</h1>
          <p className="adm__gate-msg">Not authorized.</p>
          <p className="adm__gate-mono">{shortMint(address)}</p>
        </div>
      </div>
    );
  }
  if (!signedIn) {
    return (
      <div className="adm">
        <div className="adm__gate">
          <h1 className="adm__gate-title">LOGIN</h1>
          <button className="adm__btn adm__btn--primary" disabled={loading} onClick={refresh}>
            {loading ? "Signing…" : "Sign in"}
          </button>
          {error && <div className="adm__error">{error}</div>}
        </div>
      </div>
    );
  }

  // Main ─────────────────────────────────────────────────────────────────
  const totalPendingUsd = claimable?.total_pending_usd ?? 0;
  const totalPendingSol = claimable?.total_pending_sol ?? 0;

  return (
    <div className="adm">
      <header className="adm__header">
        <div className="adm__header-right">
          <button className="adm__btn adm__btn--ghost" onClick={openUsers}>
            Users
          </button>
          <button className="adm__btn adm__btn--ghost" onClick={refresh} disabled={loading}>
            {loading ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      </header>

      {error && <div className="adm__error">{error}</div>}

      <section className="adm__stickers">
        <Sticker
          label="Pending Claims"
          value={fmtUsd(totalPendingUsd)}
          sub={`${fmtSol(totalPendingSol)} ready`}
          accent
        />
        <Sticker
          label="Claimed Today"
          value={fmtUsd(stats?.claimed_today_usd)}
          sub={fmtSol(stats?.claimed_today_sol)}
        />
        <Sticker
          label="Claimed All-Time"
          value={fmtUsd(stats?.claimed_alltime_usd)}
          sub={fmtSol(stats?.claimed_alltime_sol)}
        />
        <Sticker
          label="Volume 24h"
          value={fmtUsd(stats?.total_volume_24h_usd)}
          sub={`${fmtCount(stats?.total_trades_24h)} trades`}
        />
        <Sticker
          label="Volume All-Time"
          value={fmtUsd(stats?.total_volume_alltime_usd)}
          sub={`${fmtCount(stats?.total_trades_alltime)} trades`}
        />
        <Sticker
          label="Slurp Keep · Lifetime"
          value={fmtUsd(buckets?.buckets?.keep?.allocated_usd)}
          sub={`${fmtSol(buckets?.buckets?.keep?.allocated_sol)} accumulated`}
          accent="orange"
        />
      </section>

      <section className="adm__panel">
        <div className="adm__panel-head">
          <h2 className="adm__panel-title">Claim Queue</h2>
          <input
            type="text"
            className="adm__search"
            placeholder="Search by symbol, name, or mint"
            value={queueQuery}
            onChange={(e) => setQueueQuery(e.target.value)}
          />
          <div className="adm__panel-sub">{fmtUsd(totalPendingUsd)} ready</div>
          <button
            className="adm__btn adm__btn--primary"
            disabled={!claimable?.pools?.length || claimingAll || !!claimingMint}
            onClick={claimAll}
          >
            {claimingAll ? "Claiming…" : `Claim All (${claimable?.pools?.length || 0})`}
          </button>
        </div>
        <div className="adm__table-wrap adm__table-wrap--scroll">
          <table className="adm__table">
            <thead>
              <tr>
                <th>Token</th>
                <th>Mint</th>
                <th className="adm__num">Pending SOL</th>
                <th className="adm__num">Pending USD</th>
                <th className="adm__num">Age</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {!filteredPools.length ? (
                <tr><td colSpan={6} className="adm__empty">
                  {claimable?.pools?.length
                    ? `No pools match "${queueQuery}".`
                    : "Nothing above the dust threshold."}
                </td></tr>
              ) : filteredPools.map((p) => (
                <tr key={p.mint}>
                  <td>
                    <div className="adm__token">
                      {p.image_url
                        ? <img src={p.image_url} alt="" className="adm__token-img" />
                        : <span className="adm__token-ph" />}
                      <span className="adm__token-name">{p.symbol || p.name || "—"}</span>
                    </div>
                  </td>
                  <td className="adm__mono">{shortMint(p.mint)}</td>
                  <td className="adm__num">{fmtSol(p.pending_sol)}</td>
                  <td className="adm__num adm__num--accent">{fmtUsd(p.pending_usd)}</td>
                  <td className="adm__num adm__muted">{fmtAgo(p.created_at)}</td>
                  <td className="adm__num">
                    <button
                      className="adm__btn adm__btn--row"
                      disabled={claimingAll || !!claimingMint}
                      onClick={() => claimOne(p)}
                    >
                      {claimingMint === p.mint ? "…" : "Claim"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="adm__panel">
        <div className="adm__panel-head">
          <h2 className="adm__panel-title">Recent Claims</h2>
          {recentOpen && (
            <input
              type="text"
              className="adm__search"
              placeholder="Search by symbol, mint, or signature"
              value={recentQuery}
              onChange={(e) => setRecentQuery(e.target.value)}
            />
          )}
          <button
            type="button"
            className={`adm__toggle ${recentOpen ? "is-open" : ""}`}
            onClick={() => setRecentOpen((v) => !v)}
            aria-expanded={recentOpen}
          >
            {recentOpen ? "Hide" : "Show"} <ChevronDown size={16} />
          </button>
        </div>
        {recentOpen && (
        <div className="adm__table-wrap adm__table-wrap--scroll">
          <table className="adm__table">
            <thead>
              <tr>
                <th>When</th>
                <th>Token</th>
                <th>Mint</th>
                <th className="adm__num">SOL</th>
                <th className="adm__num">USD</th>
                <th>Tx</th>
              </tr>
            </thead>
            <tbody>
              {!filteredRecent.length ? (
                <tr><td colSpan={6} className="adm__empty">
                  {recent?.claims?.length
                    ? `No claims match "${recentQuery}".`
                    : "No claims yet."}
                </td></tr>
              ) : filteredRecent.map((c) => (
                <tr key={c.signature}>
                  <td className="adm__muted">{fmtAgo(c.ts)}</td>
                  <td>{c.symbol || "—"}</td>
                  <td className="adm__mono">{shortMint(c.mint)}</td>
                  <td className="adm__num">{fmtSol(c.sol)}</td>
                  <td className="adm__num adm__num--accent">{fmtUsd(c.usd)}</td>
                  <td className="adm__mono">
                    <a
                      href={`https://solscan.io/tx/${c.signature}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="adm__link"
                    >
                      {shortSig(c.signature)}
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}
      </section>

      {/* Jackpot · monthly draw ────────────────────────────────────── */}
      <section className="adm__panel">
        {jpUnpaidDrafts.length > 0 && (
          <div className="adm__draft-banner">
            <div className="adm__draft-banner-head">
              <span className="adm__draft-banner-label">
                {jpUnpaidDrafts.length === 1 ? "1 UNPAID DRAFT" : `${jpUnpaidDrafts.length} UNPAID DRAFTS`}
              </span>
              <span className="adm__draft-banner-sub">
                drawn winners not yet paid out
              </span>
            </div>
            <div className="adm__draft-banner-list">
              {jpUnpaidDrafts.map((d) => (
                <div key={d.id} className="adm__draft-banner-rowwrap">
                  <button
                    type="button"
                    className="adm__draft-banner-row"
                    onClick={() => setJpMonth(d.month)}
                    title={`Load ${d.month} to pay this winner`}
                  >
                    <span className="adm__draft-banner-month">{d.month}</span>
                    <span className="adm__draft-banner-wallet">
                      {d.winner_wallet.slice(0, 4)}…{d.winner_wallet.slice(-4)}
                    </span>
                    <span className="adm__draft-banner-amount">
                      {fmtUsd(d.amount_usd)} · {fmtSol(d.amount_sol)}
                    </span>
                  </button>
                  <button
                    type="button"
                    className="adm__draft-banner-del"
                    onClick={(e) => { e.stopPropagation(); askDeleteJackpotDraft(d); }}
                    title="Delete this draft"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="adm__panel-head">
          <h2 className="adm__panel-title">Jackpot · Monthly Draw</h2>
          <div className="adm__countdown">
            <span className="adm__countdown-label">NEXT DRAW</span>
            <span className="adm__countdown-value">
              {fmtCountdown(nextJackpotDraw)}
            </span>
            <span className="adm__countdown-date">
              {nextJackpotDraw?.toLocaleDateString("en-US", { month: "short", day: "numeric" })}
            </span>
          </div>
          <input
            type="month"
            className="adm__search"
            value={jpMonth}
            onChange={(e) => setJpMonth(e.target.value)}
            style={{ maxWidth: 160 }}
          />
          <div className="adm__panel-sub">
            {jpEligible
              ? `${jpEligible.eligible_count.toLocaleString()} eligible (≥$100 buys)`
              : "Loading…"}
          </div>
        </div>

        {!jpWinner ? (
          <div className="adm__jp-grid">
            <div className="adm__jp-stat">
              <div className="adm__jp-stat-label">POT TO PAY OUT</div>
              <div className="adm__jp-stat-value">
                {fmtUsd(buckets?.buckets?.jackpot?.owed_usd)}
              </div>
              <div className="adm__jp-stat-sub">
                {fmtSol(buckets?.buckets?.jackpot?.owed_sol)}
              </div>
            </div>
            <div className="adm__jp-stat">
              <div className="adm__jp-stat-label">ELIGIBLE WALLETS</div>
              <div className="adm__jp-stat-value">
                {jpEligible?.eligible_count?.toLocaleString() ?? "—"}
              </div>
              <div className="adm__jp-stat-sub">
                spent ≥$100 in {jpMonth}
              </div>
            </div>
            <button
              type="button"
              className="adm__btn adm__btn--primary adm__jp-draw-btn"
              disabled={
                jpDrawing ||
                !jpEligible?.eligible_count ||
                !buckets?.buckets?.jackpot?.owed_sol
              }
              onClick={openDrawConfirm}
            >
              {jpDrawing ? "Drawing…" : "Draw Winner"}
            </button>
          </div>
        ) : (
          <div className="adm__jp-winner">
            <div className="adm__jp-winner-label">DRAWN · UNPAID</div>
            <div className="adm__jp-winner-wallet">
              <a
                href={`https://solscan.io/account/${jpWinner.winner_wallet}`}
                target="_blank"
                rel="noopener noreferrer"
                className="adm__link"
              >
                {jpWinner.winner_wallet}
              </a>
            </div>
            <div className="adm__jp-winner-meta">
              Pot to pay: <b style={{ color: "#fae039" }}>
                {fmtUsd(buckets?.buckets?.jackpot?.owed_usd)}
              </b>{" "}({fmtSol(buckets?.buckets?.jackpot?.owed_sol)})
              {" · "}1 of {jpWinner.qualifying_wallets?.toLocaleString?.() ?? "?"}
            </div>
            <button
              type="button"
              className="adm__btn adm__btn--primary"
              disabled={jpPaying || (buckets?.buckets?.jackpot?.owed_sol ?? 0) <= 0}
              onClick={payJackpotAuto}
            >
              {jpPaying
                ? "Sending…"
                : `Pay Winner (${fmtUsd(buckets?.buckets?.jackpot?.owed_usd)})`}
            </button>
          </div>
        )}
      </section>

      {/* Diamond Hands prize-pool snapshots ────────────────────────── */}
      <section className="adm__panel">
        <div className="adm__panel-head">
          <h2 className="adm__panel-title">Diamond Hands · Weekly Winners</h2>
          {nextLeaderboardSnapshot && (
            <div className="adm__countdown">
              <span className="adm__countdown-label">NEXT SNAPSHOT</span>
              <span className="adm__countdown-value">
                {fmtCountdown(nextLeaderboardSnapshot)}
              </span>
              <span className="adm__countdown-date">
                {nextLeaderboardSnapshot.toLocaleDateString("en-US", { month: "short", day: "numeric" })}
              </span>
            </div>
          )}
          <div className="adm__panel-sub">
            Snapshots taken every 7 days. Pay out via the auto-pay button per week.
          </div>
        </div>
        <div className="adm__table-wrap adm__table-wrap--scroll">
          <table className="adm__table">
            <thead>
              <tr>
                <th>Week start (UTC)</th>
                <th className="adm__num">Winners</th>
                <th className="adm__num">Paid</th>
                <th className="adm__num">Total paid (SOL)</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {(() => {
                // Hide weeks where every winner is paid — operator only
                // needs to see what still owes money.
                const pending = snapshotWeeks.filter((w) => w.paid_count < w.winner_count);
                if (pending.length === 0) {
                  return (
                    <tr><td colSpan={5} className="adm__empty">
                      {snapshotWeeks.length === 0
                        ? "No snapshots yet (the worker takes the first one ~7 days after launch)."
                        : "All winners paid. Next snapshot drops on the 7-day cycle."}
                    </td></tr>
                  );
                }
                return pending.map((w) => (
                  <tr key={w.week_start}>
                    <td className="adm__mono">{new Date(w.week_start).toISOString().slice(0, 16).replace("T", " ")}</td>
                    <td className="adm__num">{w.winner_count}</td>
                    <td className="adm__num">
                      {w.paid_count} / {w.winner_count}
                    </td>
                    <td className="adm__num">{Number(w.total_paid_sol || 0).toFixed(4)}</td>
                    <td className="adm__lb-row-actions">
                      <button
                        className="adm__btn"
                        disabled={winnersLoading && activeWeek === w.week_start}
                        onClick={() => openWeek(w.week_start)}
                      >
                        {winnersLoading && activeWeek === w.week_start
                          ? "Loading…"
                          : activeWeek === w.week_start ? "Refresh" : "Open"}
                      </button>
                      <button
                        className="adm__btn adm__btn--ghost"
                        onClick={() => askDeleteLeaderboardWeek(w.week_start)}
                        title="Delete this whole week's snapshot"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ));
              })()}
            </tbody>
          </table>
        </div>

        {activeWeek && (
          <div style={{ marginTop: 14 }}>
            <div className="adm__lb-week-bar">
              <div>
                <div className="adm__lb-week-label">
                  WEEK OF {new Date(activeWeek).toISOString().slice(0, 10)}
                  {winnersLoading ? " · loading…" : ""}
                </div>
                <div className="adm__lb-pot-info">
                  Pot to split:{" "}
                  <b>{fmtUsd(activePot?.pot_owed_usd)}</b>{" "}
                  ({fmtSol(activePot?.pot_owed_sol)})
                  {" · "}top-heavy split: 30/20/14/10/8/6/4/3.5/2.5/2
                </div>
              </div>
              <div className="adm__lb-week-actions">
                {(() => {
                  const unpaid = (activeWinners ?? []).filter(
                    (w) => !w.paid_at && (w.suggested_payout_sol ?? 0) > 0
                  );
                  if (unpaid.length === 0) return null;
                  return (
                    <button
                      type="button"
                      className="adm__btn adm__btn--primary"
                      disabled={autoPaying || winnersLoading}
                      onClick={openLeaderboardAutoPay}
                    >
                      {autoPaying
                        ? "Sending…"
                        : `Pay All ${unpaid.length} Winners`}
                    </button>
                  );
                })()}
                <button
                  type="button"
                  className="adm__btn adm__btn--ghost"
                  disabled={autoPaying}
                  onClick={() => {
                    setActiveWeek(null);
                    setActiveWinners(null);
                    setActivePot(null);
                  }}
                >
                  Close
                </button>
              </div>
            </div>
            <div className="adm__table-wrap adm__table-wrap--scroll">
              <table className="adm__table">
                <thead>
                  <tr>
                    <th className="adm__num">#</th>
                    <th>Wallet</th>
                    <th className="adm__num">Score</th>
                    <th className="adm__num">Pay them</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    // Only show unpaid winners — once paid they drop out
                    // of the list. Full audit trail lives in the DB.
                    const unpaid = (activeWinners ?? []).filter((w) => !w.paid_at);
                    if (unpaid.length === 0) {
                      return (
                        <tr><td colSpan={5} className="adm__empty">
                          All winners for this week have been paid.
                        </td></tr>
                      );
                    }
                    return unpaid.map((w) => {
                      const payUsd = (w.suggested_payout_usd ?? 0);
                      const paySol = (w.suggested_payout_sol ?? 0);
                      return (
                        <tr key={w.id}>
                          <td className="adm__num adm__lb-rank">{w.rank}</td>
                          <td className="adm__mono">{shortMint(w.wallet)}</td>
                          <td className="adm__num">{Number(w.score).toFixed(2)}</td>
                          <td className="adm__num">
                            {paySol > 0 ? (
                              <span className="adm__lb-pay">
                                {fmtUsd(payUsd)}
                                <span className="adm__lb-pay-sub">
                                  {fmtSol(paySol)}
                                  {" · "}{((w.rank_pct ?? 0) * 100).toFixed(1)}%
                                </span>
                              </span>
                            ) : (
                              <span className="adm__muted">pot empty</span>
                            )}
                          </td>
                          <td colSpan={2}>
                            <span className="adm__muted">unpaid</span>
                          </td>
                        </tr>
                      );
                    });
                  })()}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </section>

      {/* Platform-wide broadcast composer ──────────────────────────── */}
      <section className="adm__panel">
        <div className="adm__panel-head">
          <h2 className="adm__panel-title">Broadcast Notification</h2>
          <div className="adm__panel-sub">
            Push a single announcement to every wallet's bell. Use sparingly, this lights up everyone.
          </div>
        </div>
        <div className="adm__broadcast">
          <input
            type="text"
            className="adm__input"
            placeholder="Title (max 200 chars)"
            value={bcTitle}
            onChange={(e) => setBcTitle(e.target.value.slice(0, 200))}
            disabled={bcSending}
          />
          <textarea
            className="adm__textarea"
            placeholder="Body (optional, max 2000 chars)"
            value={bcBody}
            onChange={(e) => setBcBody(e.target.value.slice(0, 2000))}
            rows={3}
            disabled={bcSending}
          />
          <input
            type="url"
            className="adm__input"
            placeholder="Link (optional, must start with https://)"
            value={bcLink}
            onChange={(e) => setBcLink(e.target.value.slice(0, 500))}
            disabled={bcSending}
          />
          <div className="adm__broadcast-actions">
            <button
              type="button"
              className="adm__btn adm__btn--primary"
              onClick={openBroadcastConfirm}
              disabled={bcSending || !bcTitle.trim()}
            >
              {bcSending ? "Sending…" : "Broadcast to everyone"}
            </button>
            {bcLastSentAt && (
              <span className="adm__broadcast-last">
                Last sent {bcLastSentAt.toLocaleTimeString()}
              </span>
            )}
          </div>
          {bcError && <div className="adm__error" style={{ marginTop: 10 }}>{bcError}</div>}
        </div>
      </section>

      {jpConfirmDraw && (
        <ConfirmModal
          title={`Draw a winner for ${jpMonth}?`}
          body={`1 random wallet will be picked from ${jpEligible?.eligible_count ?? 0} eligible wallets. This is final, no re-draws for this month.`}
          confirmLabel="Draw Winner"
          submitting={jpDrawing}
          onConfirm={drawJackpotWinner}
          onClose={() => setJpConfirmDraw(false)}
        />
      )}
      {bcConfirm && (
        <ConfirmModal
          title="Send announcement to every wallet?"
          body={`"${bcConfirm.title}"${bcConfirm.body ? `\n\n${bcConfirm.body}` : ""}`}
          confirmLabel="Send to everyone"
          submitting={bcSending}
          onConfirm={sendBroadcast}
          onClose={() => !bcSending && setBcConfirm(null)}
        />
      )}
      {deleteConfirm && (
        <ConfirmModal
          title={deleteConfirm.title}
          body={deleteConfirm.body}
          confirmLabel="Delete"
          danger
          submitting={deleting}
          onConfirm={deleteConfirm.onConfirm}
          onClose={() => !deleting && setDeleteConfirm(null)}
        />
      )}
      {lbPayModal && (
        <MarkPaidModal
          title={`Mark rank ${lbPayModal.winner.rank} paid`}
          subtitle={`Wallet: ${lbPayModal.winner.wallet}`}
          defaultAmount={lbPayModal.defaultAmt}
          submitting={payingId === lbPayModal.winner.id}
          onSubmit={submitLeaderboardWinnerPaid}
          onClose={() => setLbPayModal(null)}
        />
      )}
      {autoPayConfirm && (
        <AdminModal onClose={() => !autoPaying && setAutoPayConfirm(null)}>
          <h3 className="adm-modal-title">Pay {autoPayConfirm.payouts.length} winners</h3>
          <p className="adm-modal-body">
            One signed tx, one Phantom popup. Treasury sends SOL to each
            winner; the DB records all rows paid with the same tx sig.
          </p>
          <div className="adm-modal-list">
            {autoPayConfirm.payouts.map((p) => (
              <div key={p.id} className="adm-modal-list-row">
                <span className="adm-modal-list-rank">#{p.rank}</span>
                <span className="adm-modal-list-wallet">
                  {p.wallet.slice(0, 4)}…{p.wallet.slice(-4)}
                </span>
                <span className="adm-modal-list-amount">
                  {p.amount_sol.toFixed(6)} SOL
                </span>
              </div>
            ))}
            <div className="adm-modal-list-total">
              <span>TOTAL</span>
              <span>{autoPayConfirm.totalSol.toFixed(6)} SOL</span>
            </div>
          </div>
          <div className="adm-modal-actions">
            <button
              type="button"
              className="adm__btn adm__btn--ghost"
              disabled={autoPaying}
              onClick={() => setAutoPayConfirm(null)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="adm__btn adm__btn--primary"
              disabled={autoPaying}
              onClick={payAllLeaderboard}
            >
              {autoPaying ? "Sending tx…" : "Sign & Send"}
            </button>
          </div>
        </AdminModal>
      )}

      {usersOpen && (
        <UsersModal ctx={ctx} onClose={() => setUsersOpen(false)} />
      )}
    </div>
  );
}

const PAGE_SIZE = 20;

function UsersModal({ ctx, onClose }) {
  const [rows, setRows] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [err, setErr] = useState(null);
  const [sort, setSort] = useState("alltime");
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [copied, setCopied] = useState(null);

  // Debounce search to avoid a query on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 300);
    return () => clearTimeout(t);
  }, [query]);

  // Reset + refetch whenever sort or search changes.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErr(null);
    adminApi
      .users(ctx, { sort, q: debounced, limit: PAGE_SIZE, offset: 0 })
      .then((r) => {
        if (cancelled) return;
        setRows(r?.users ?? []);
        setHasMore(!!r?.has_more);
        setOffset(PAGE_SIZE);
      })
      .catch((e) => { if (!cancelled) setErr(e?.message || String(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [ctx, sort, debounced]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const r = await adminApi.users(ctx, {
        sort, q: debounced, limit: PAGE_SIZE, offset,
      });
      setRows((prev) => [...prev, ...(r?.users ?? [])]);
      setHasMore(!!r?.has_more);
      setOffset((o) => o + PAGE_SIZE);
    } catch (e) {
      setErr(e?.message || String(e));
    } finally {
      setLoadingMore(false);
    }
  };

  const copyWallet = (w) => {
    navigator.clipboard?.writeText(w).then(() => {
      setCopied(w);
      setTimeout(() => setCopied((cur) => (cur === w ? null : cur)), 1200);
    });
  };

  const exportCsv = () => {
    const header = "wallet,username,volume_week_usd,volume_month_usd,volume_alltime_usd,trades_week,trades_month,trades_alltime,first_trade_at,last_trade_at";
    const lines = rows.map((u) =>
      [
        u.wallet,
        u.username || "",
        Number(u.volume_week_usd || 0).toFixed(2),
        Number(u.volume_month_usd || 0).toFixed(2),
        Number(u.volume_alltime_usd || 0).toFixed(2),
        u.trades_week,
        u.trades_month,
        u.trades_alltime,
        u.first_trade_at || "",
        u.last_trade_at || "",
      ].join(",")
    );
    const csv = [header, ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `slurp-users-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <AdminModal onClose={onClose}>
      <h3 className="adm-modal-title">Users · trading volume</h3>
      <div className="adm-modal-body" style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 8 }}>
        <input
          type="text"
          className="adm__search"
          placeholder="Search wallet or username"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          style={{ flex: "1 1 220px", minWidth: 200 }}
        />
        <div style={{ display: "flex", gap: 4 }}>
          {[
            { v: "week",    label: "7d"  },
            { v: "month",   label: "MTD" },
            { v: "alltime", label: "1y"  },
          ].map((opt) => (
            <button
              key={opt.v}
              type="button"
              className={`adm__btn ${sort === opt.v ? "adm__btn--primary" : "adm__btn--ghost"}`}
              onClick={() => setSort(opt.v)}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <button type="button" className="adm__btn adm__btn--ghost" onClick={exportCsv} disabled={!rows.length}>
          Export loaded
        </button>
        <div className="adm-modal-body" style={{ marginLeft: "auto" }}>
          {rows.length.toLocaleString()} loaded{hasMore ? "+" : ""}
        </div>
      </div>
      {loading ? (
        <div className="adm-modal-body">Loading…</div>
      ) : err ? (
        <div className="adm__error">{err}</div>
      ) : (
        <>
          <div className="adm__table-wrap adm__table-wrap--scroll" style={{ maxHeight: "55vh" }}>
            <table className="adm__table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Wallet</th>
                  <th className="adm__num">7d vol</th>
                  <th className="adm__num">MTD vol</th>
                  <th className="adm__num">1y vol</th>
                  <th className="adm__num">Trades</th>
                  <th className="adm__num">Last</th>
                </tr>
              </thead>
              <tbody>
                {!rows.length ? (
                  <tr><td colSpan={7} className="adm__empty">
                    {debounced ? `No wallets match "${debounced}".` : "No trading activity yet."}
                  </td></tr>
                ) : rows.map((u, i) => (
                  <tr key={u.wallet}>
                    <td>{i + 1}</td>
                    <td>
                      <div className="adm__token">
                        {u.image_url
                          ? <img src={u.image_url} alt="" className="adm__token-img" />
                          : <div className="adm__token-img adm__token-img--ph" />}
                        <div>
                          {u.username && <div>@{u.username}</div>}
                          <button
                            type="button"
                            onClick={() => copyWallet(u.wallet)}
                            title={`Click to copy ${u.wallet}`}
                            style={{
                              background: "none",
                              border: "none",
                              padding: 0,
                              cursor: "pointer",
                              fontFamily: "monospace",
                              fontSize: 12,
                              opacity: copied === u.wallet ? 1 : 0.7,
                              color: copied === u.wallet ? "#48f7f7" : "inherit",
                            }}
                          >
                            {copied === u.wallet
                              ? "copied!"
                              : `${u.wallet.slice(0, 4)}…${u.wallet.slice(-4)}`}
                          </button>
                        </div>
                      </div>
                    </td>
                    <td className="adm__num">${Number(u.volume_week_usd || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                    <td className="adm__num">${Number(u.volume_month_usd || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                    <td className="adm__num">${Number(u.volume_alltime_usd || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                    <td className="adm__num">{Number(u.trades_alltime || 0).toLocaleString()}</td>
                    <td className="adm__num" style={{ opacity: 0.7 }}>
                      {u.last_trade_at ? new Date(u.last_trade_at).toLocaleDateString() : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {hasMore && (
            <div style={{ textAlign: "center", marginTop: 10 }}>
              <button
                type="button"
                className="adm__btn adm__btn--ghost"
                onClick={loadMore}
                disabled={loadingMore}
              >
                {loadingMore ? "Loading…" : `Load more (${PAGE_SIZE})`}
              </button>
            </div>
          )}
        </>
      )}
    </AdminModal>
  );
}
